import React, { useState } from 'react'
import { Turbo } from '@hotwired/turbo-rails'
import { showSuccess, showError } from '../utils/notifications.js'
import ClientSelect from './ClientSelect.jsx'
import FundSelect from './FundSelect.jsx'
import FundAssetSelect from './FundAssetSelect.jsx'
import ProgressIndicator from './ProgressIndicator.jsx'
import { t } from '../../i18n.js'

// Main New Valuation form component
export default function NewValuation({ 
  backUrl, 
  submitUrl, 
  clientsSearchUrl,
  clientsCreateUrl,
  fundsSearchUrl,
  fundsCreateUrl,
  fundAssetsSearchUrl,
  fundAssetsCreateUrl,
  fundAssetsBulkCreateUrl,
  csrfToken,
  translations
}) {
  const [mode, setMode] = useState('new') // 'new' or 'roll_forward'
  const [valuationDate, setValuationDate] = useState('')
  const [clientId, setClientId] = useState(null)
  const [clientName, setClientName] = useState(null)
  const [fundId, setFundId] = useState(null)
  const [fundName, setFundName] = useState(null)
  const [fundAssetIds, setFundAssetIds] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate form
    if (!valuationDate) {
      showError(t('validation.date_required', translations))
      return
    }
    if (!fundId) {
      showError(t('validation.fund_required', translations))
      return
    }
    if (fundAssetIds.length === 0) {
      showError(t('validation.assets_required', translations))
      return
    }
    
    setIsSubmitting(true)
    
    try {
      // Extract only IDs from fund asset objects
      const assetIds = fundAssetIds.map(asset => 
        typeof asset === 'object' ? asset.value : asset
      )
      
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          valuation_group: {
            valuation_date: valuationDate,
            fund_id: fundId,
            fund_asset_ids: assetIds
          }
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        showSuccess(t('submit.success', translations))
        Turbo.visit(data.redirect_url)
      } else {
        const errorMessages = data.errors ? Object.values(data.errors).flat().join(', ') : t('submit.failure', translations)
        showError(errorMessages)
        setIsSubmitting(false)
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      showError(t('submit.exception', translations))
      setIsSubmitting(false)
    }
  }

  return (
    <div className="row">
      <div className="col-lg-8">
        {/* Mode Selection */}
        <div className="mode-selector">
          <div 
            className={`mode-selector__card ${mode === 'new' ? 'mode-selector__card--active' : ''}`}
            onClick={() => setMode('new')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setMode('new')}
          >
            <div className="mode-selector__icon">
              <i className="bi bi-plus-lg"></i>
            </div>
            <div className="mode-selector__content">
              <div className="mode-selector__title">{t('mode.start_new.title', translations)}</div>
              <div className="mode-selector__subtitle">{t('mode.start_new.subtitle', translations)}</div>
            </div>
            {mode === 'new' && (
              <i className="bi bi-check-circle-fill mode-selector__check"></i>
            )}
          </div>

          <div 
            className={`mode-selector__card ${mode === 'roll_forward' ? 'mode-selector__card--active' : ''}`}
            onClick={() => setMode('roll_forward')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setMode('roll_forward')}
          >
            <div className="mode-selector__icon">
              <i className="bi bi-arrow-counterclockwise"></i>
            </div>
            <div className="mode-selector__content">
              <div className="mode-selector__title">{t('mode.roll_forward.title', translations)}</div>
              <div className="mode-selector__subtitle">{t('mode.roll_forward.subtitle', translations)}</div>
            </div>
            {mode === 'roll_forward' && (
              <i className="bi bi-check-circle-fill mode-selector__check"></i>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {mode === 'roll_forward' ? (
              <div className="roll-forward-placeholder">
                <i className="bi bi-arrow-clockwise roll-forward-placeholder__icon"></i>
                <p className="roll-forward-placeholder__text">{t('mode.roll_forward.coming_soon', translations)}</p>
                <a href={backUrl} className="btn btn-secondary">
                  {t('cancel', translations)}
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <ClientSelect
                  value={clientId}
                  onChange={(id, name) => {
                    setClientId(id)
                    setClientName(name)
                    setFundId(null)
                    setFundName(null)
                    setFundAssetIds([])
                  }}
                  searchUrl={clientsSearchUrl}
                  createUrl={clientsCreateUrl}
                  csrfToken={csrfToken}
                  translations={translations.clientSelect}
                />

                <FundSelect
                  clientId={clientId}
                  value={fundId}
                  onChange={(id, name) => {
                    setFundId(id)
                    setFundName(name)
                    setFundAssetIds([])
                  }}
                  searchUrl={fundsSearchUrl}
                  createUrl={fundsCreateUrl}
                  disabled={!clientId}
                  csrfToken={csrfToken}
                  translations={translations.fundSelect}
                />

                <div className="mb-3">
                  <label className="form-label">{t('valuation_date.label', translations)} <span className="text-danger">*</span></label>
                  {/* EVA-155: native browser date input (like Rent Review) — no
                      pre-filled today, calendar on demand, ISO value. */}
                  <input
                    type="date"
                    className="form-control"
                    value={valuationDate}
                    onChange={(e) => setValuationDate(e.target.value)}
                    required
                  />
                </div>

                <FundAssetSelect
                  fundId={fundId}
                  values={fundAssetIds}
                  onChange={setFundAssetIds}
                  searchUrl={fundAssetsSearchUrl}
                  createUrl={fundAssetsCreateUrl}
                  bulkCreateUrl={fundAssetsBulkCreateUrl}
                  disabled={!fundId}
                  csrfToken={csrfToken}
                  hideImport={true}
                  translations={translations.fundAssetSelect}
                />

                <div className="d-flex gap-2 mt-3">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('creating', translations)}
                      </>
                    ) : (
                      fundAssetIds.length >= 2
                        ? t('submit_button.create_valuations', translations)
                        : t('submit_button.create_valuation', translations)
                    )}
                  </button>
                  <a href={backUrl} className="btn btn-secondary">
                    {t('cancel', translations)}
                  </a>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      
      <div className="col-lg-4">
        <ProgressIndicator
          clientName={clientName}
          fundName={fundName}
          hasAssets={fundAssetIds.length > 0}
          valuationDate={valuationDate}
          translations={translations.progress}
        />
      </div>
    </div>
  )
}

